import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { getTeamColorsAPI } from '@api/gettersMartech';
import { getFixturesTeamColors } from '@api/gettersMockData';
import { getUrlParam } from '@utils/functions';
import { UrlParams } from '@utils/enums';

type BaseApiColors = { homeColor: string; awayColor: string } | null;

export interface Readiness {
  colors: boolean;
  minimized: boolean; 
  stats: boolean; 
  teamNames: boolean;
  overview: boolean;
  players: boolean;
  lineup: boolean;
  h2h: boolean;
}

interface OrchestratorState {
  ready: Readiness;
  apiColors: BaseApiColors;
  initializing: boolean;
  error: string | null;
}

type ReadyKey = keyof Readiness;

type OrchestratorAction =
  | { type: 'INIT_START' }
  | { type: 'SET_COLORS'; payload: BaseApiColors }
  | { type: 'SET_READY'; payload: { key: ReadyKey; value: boolean } }
  | { type: 'RESET' }
  | { type: 'SET_ERROR'; payload: string | null };

const initialState: OrchestratorState = {
  ready: {
    colors: false,
    minimized: false,
    stats: false,
    teamNames: false,
    overview: false,
    players: false,
    lineup: false,
    h2h: false,
  },
  apiColors: null,
  initializing: false,
  error: null,
};

const reducer = (state: OrchestratorState, action: OrchestratorAction): OrchestratorState => {
  switch (action.type) {
    case 'INIT_START':
      return { ...initialState, initializing: true };
    case 'SET_COLORS':
      return {
        ...state,
        apiColors: action.payload,
        ready: { ...state.ready, colors: !!action.payload },
        initializing: false,
        error: null,
      };
    case 'SET_READY':
      return { ...state, ready: { ...state.ready, [action.payload.key]: action.payload.value } };
    case 'SET_ERROR':
      return { ...state, error: action.payload, initializing: false };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

interface DataOrchestratorContextType {
  ready: Readiness;
  apiColors: BaseApiColors;
  init: (params?: { eventId?: string | null; isFixtures?: boolean }) => Promise<void>;
}

const DataOrchestratorContext = createContext<DataOrchestratorContextType | undefined>(undefined);

export const DataOrchestratorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const init = useCallback(async (params?: { eventId?: string | null; isFixtures?: boolean }) => {
    dispatch({ type: 'INIT_START' });
    const eventId = params?.eventId ?? getUrlParam(UrlParams.EVENT_ID);
    const fixturesParam = params?.isFixtures ?? getUrlParam(UrlParams.FIXTURES);
    const fixtures = fixturesParam === true || fixturesParam === 'true';
    
    try {
      let baseColors: BaseApiColors = { homeColor: '#147B45', awayColor: '#333333' };
      if (eventId && !fixtures) {
        const result = await getTeamColorsAPI(eventId);
        baseColors = {
          homeColor: result.data?.homeColor ?? '#147B45',
          awayColor: result.data?.awayColor ?? '#333333',
        };
      } else if (eventId && fixtures) {
        const result = await getFixturesTeamColors(eventId);
        baseColors = {
          homeColor: result.data?.homeColor ?? '#147B45',
          awayColor: result.data?.awayColor ?? '#333333',
        };
      }
      dispatch({ type: 'SET_COLORS', payload: baseColors });

      // Priority sequence after colors
      dispatch({ type: 'SET_READY', payload: { key: 'minimized', value: true } });
      dispatch({ type: 'SET_READY', payload: { key: 'stats', value: true } });
      dispatch({ type: 'SET_READY', payload: { key: 'teamNames', value: true } });

      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'overview', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'players', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'lineup', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'h2h', value: true } }), 0);
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Unknown error' });
      // Even with an error, we continue with default colors and enable the rest
      dispatch({ type: 'SET_COLORS', payload: { homeColor: '#147B45', awayColor: '#333333' } });
      dispatch({ type: 'SET_READY', payload: { key: 'minimized', value: true } });
      dispatch({ type: 'SET_READY', payload: { key: 'stats', value: true } });
      dispatch({ type: 'SET_READY', payload: { key: 'teamNames', value: true } });
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'overview', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'players', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'lineup', value: true } }), 0);
      setTimeout(() => dispatch({ type: 'SET_READY', payload: { key: 'h2h', value: true } }), 0);
    }
  }, []);

  // Automatic initialization after changing URL parameters
  useEffect(() => {
    const eventId = getUrlParam(UrlParams.EVENT_ID);
    const fixturesParam = getUrlParam(UrlParams.FIXTURES);
    const fixtures = fixturesParam === 'true';
    init({ eventId, isFixtures: fixtures });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrlParam(UrlParams.EVENT_ID), getUrlParam(UrlParams.FIXTURES)]);

  const value = useMemo<DataOrchestratorContextType>(() => ({
    ready: state.ready,
    apiColors: state.apiColors,
    init,
  }), [state.ready, state.apiColors, init]);

  return (
    <DataOrchestratorContext.Provider value={value}>
      {children}
    </DataOrchestratorContext.Provider>
  );
};

export const useDataOrchestrator = (): DataOrchestratorContextType => {
  const ctx = useContext(DataOrchestratorContext);
  if (!ctx) throw new Error('useDataOrchestrator must be used within a DataOrchestratorProvider');
  return ctx;
};

